// AdminUserDetailPage.js - /admin/users/:phone
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import Layout from '../Layout';
import { AdminLoadingBar, AdminLoadingStyles, AdminSpinner } from './AdminLoadingUi';

async function adminFetch(path, opts = {}) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(path, { ...opts, headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export default function AdminUserDetailPage() {
  const BUSY_DOTS = '\u002E\u002E\u002E';
  const { phone } = useParams();
  const decodedPhone = decodeURIComponent(phone);
  const encodedPhone = encodeURIComponent(decodedPhone);

  const [tab, setTab] = useState('activity');
  const [activity, setActivity] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      adminFetch(`/api/admin/users/${encodedPhone}/activity`),
      adminFetch(`/api/admin/users/${encodedPhone}/sessions`),
      adminFetch('/api/admin/users'),
    ])
      .then(([act, sess, usersData]) => {
        setActivity(act.logs);
        setSessions(sess.sessions);
        const found = usersData.users.find((u) => u.phone === decodedPhone);
        setBlocked(found ? found.blocked : false);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [encodedPhone, decodedPhone]);

  async function blockToggle() {
    const action = blocked ? 'unblock' : 'block';
    if (!window.confirm(`${blocked ? 'Unblock' : 'Block'} ${decodedPhone}?`)) return;
    setBusy(true);
    try {
      await adminFetch(`/api/admin/users/${encodedPhone}/${action}`, { method: 'POST' });
      setBlocked(!blocked);
    } catch (e) {
      alert('Failed: ' + e.message);
    }
    setBusy(false);
  }

  async function revokeAll() {
    if (!window.confirm(`Force logout ${decodedPhone} from ALL devices?`)) return;
    setBusy(true);
    try {
      const data = await adminFetch(`/api/admin/users/${encodedPhone}/revoke-all`, { method: 'POST' });
      alert(data.message || 'Done');
      setSessions([]);
    } catch (e) {
      alert('Failed: ' + e.message);
    }
    setBusy(false);
  }

  const page = { fontFamily: 'sans-serif', padding: '28px 32px', maxWidth: 1100, margin: '0 auto', color: '#1e293b' };
  const tabBtn = (t) => ({
    padding: '8px 18px',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
    background: 'transparent',
    color: tab === t ? '#3b82f6' : '#64748b',
  });
  const btn = (color) => ({
    fontSize: 13,
    padding: '6px 14px',
    border: `1px solid ${color}`,
    borderRadius: 5,
    cursor: 'pointer',
    background: '#fff',
    color,
    marginRight: 8,
  });

  return (
    <Layout>
      <div style={page}>
        <AdminLoadingStyles />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <Link to="/admin/users" style={{ color: '#64748b', textDecoration: 'none', fontSize: 14 }}>
            {'← Users'}
          </Link>
          <h1 style={{ margin: 0, fontSize: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>{decodedPhone}</span>
            <AdminSpinner loading={loading} />
          </h1>
          {blocked !== null ? (
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 10,
                background: blocked ? '#fee2e2' : '#dcfce7',
                color: blocked ? '#dc2626' : '#16a34a',
              }}
            >
              {blocked ? 'BLOCKED' : 'Active'}
            </span>
          ) : null}
        </div>
        <AdminLoadingBar loading={loading} />

        <div style={{ marginBottom: 20 }}>
          <button disabled={busy} onClick={blockToggle} style={btn(blocked ? '#16a34a' : '#dc2626')}>
            {busy ? BUSY_DOTS : blocked ? 'Unblock User' : 'Block User'}
          </button>
          <button disabled={busy} onClick={revokeAll} style={btn('#7c3aed')}>
            {busy ? BUSY_DOTS : 'Force Logout (All Devices)'}
          </button>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
            Force logout invalidates all active sessions across every device simultaneously. Per-device logout is not available - Firebase does not support it without client-side changes.
          </p>
        </div>

        {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>Error: {error}</div>}

        {!loading ? (
          <>
            <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: 20 }}>
              <button style={tabBtn('activity')} onClick={() => setTab('activity')}>
                Activity Log ({activity.length})
              </button>
              <button style={tabBtn('sessions')} onClick={() => setTab('sessions')}>
                Known Devices / IPs ({sessions.length})
              </button>
            </div>

            {tab === 'activity' ? (
              <>
                {activity.length === 0 ? (
                  <p style={{ color: '#64748b' }}>No activity recorded yet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        {['Time', 'Endpoint', 'Status', 'IP', 'Query'].map((h) => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((log) => (
                        <tr key={log.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: '#64748b' }}>
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                          </td>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{log.endpoint}</td>
                          <td style={{ padding: '7px 10px', color: log.status >= 400 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{log.status}</td>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{log.ip}</td>
                          <td style={{ padding: '7px 10px', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.query && Object.keys(log.query).length > 0 ? JSON.stringify(log.query) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : null}

            {tab === 'sessions' ? (
              <>
                {sessions.length === 0 ? (
                  <p style={{ color: '#64748b' }}>No session data yet. Sessions are recorded as the user makes API requests.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        {['IP', 'First Seen', 'Last Seen', 'User Agent'].map((h) => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s) => (
                        <tr key={s.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{s.ip}</td>
                          <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>
                            {s.firstSeen ? new Date(s.firstSeen).toLocaleString() : '-'}
                          </td>
                          <td style={{ padding: '7px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>
                            {s.lastSeen ? new Date(s.lastSeen).toLocaleString() : '-'}
                          </td>
                          <td style={{ padding: '7px 10px', color: '#94a3b8', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.userAgent || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </Layout>
  );
}
